import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { HttpClientBaseService } from './base/httpClientBase.service';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { PageList } from '../models/pageList.model';
import { AppConfig } from '../configuration/app.config';
import { AppA3DocConnectionService } from './appA3DocConnection.service';
import { catchError, map, switchMap } from 'rxjs/operators';

import { formatCreateA3DocJson } from '../../livesHrComponentes/helpers/formatCreateA3DocJson.js';
import { formatUpdateA3DocJson } from '../../livesHrComponentes/helpers/formatUpdateA3DocJson.js';
import { formatUpdateA3DocUsersJson } from '../../livesHrComponentes/helpers/formatUpdateA3DocUsersJson.js';
import { formatUpdateA3DocUsersOnboardingJson } from '../../livesHrComponentes/helpers/formatUpdateA3DocUsersOnboardingJson.js';
import { formatDeleteA3DocUsersJson } from '../../livesHrComponentes/helpers/formatDeleteA3DocUsersJson.js';
import { formatCategoryKeyValue } from '../../livesHrComponentes/helpers/formatCategoryTree.js';
import { AppA3DocDocument } from '../models/appA3DocDocument.model';
import { convertToISOString } from '../../livesHrComponentes/helpers/getIsoStringDate.js'
import { formatDate } from '../../livesHrComponentes/helpers/getLocalDate.js'
import { LivesSessionHelper } from '../helper/livesSessionHelper';

@Injectable({ providedIn: 'root' })
export class AppA3DocService extends HttpClientBaseService {

    constructor(
        _http: HttpClient,
        private appA3DocConnectionService: AppA3DocConnectionService,
        private _livesSession: LivesSessionHelper,

    ) {
        super(_http, '/documentmanager/', '', false);


    }


    getModel(id: any): Observable<AppA3DocDocument> {
        return super.getSingle<AppA3DocDocument>(id);
    }


    getPagedModels(parentId?: any, params?: HttpParams): Observable<PageList<any>> {
        throw new Error('Method not implemented.');
    }

    getAllModels(): Observable<any[]> {
        throw new Error('Method not implemented.');

    }

    //usa fetch para evitar el custominterceptor, ya que si la consulta da error 401 deslogea al usuario
    getToken({
        tenant,
        user,
        password,
        deviceId,
        deviceType,
        devicePlatform,
        apiKey,
        isTest = false
    }: {
        tenant?: string,
        user?: string,
        password?: string,
        deviceId?: string,
        deviceType?: string,
        devicePlatform?: string,
        apiKey?: string,
        isTest?: boolean
    } = {}): Observable<any> {
        const url = `${AppConfig.settings.apiServerPE.a3docUrl}login`;
        this._livesSession.removeA3DocToken(); // Elimina cualquier token anterior

        // Si isTest es true, utiliza los valores directamente y devuelve el Observable
        if (isTest) {
            const headers = {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': apiKey
            };

            const credentials = {
                tenant,
                user,
                password,
                deviceId: deviceId || 'TESTPC-WinUser',
                deviceType: deviceType || 0,
                devicePlatform: devicePlatform || 98
            };

            return new Observable(observer => {
                fetch(url, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(credentials)
                })
                    .then(response => {
                        if (response.status === 401) {
                            observer.error({ status: 400, message: "Error 400: Solicitud incorrecta. Revise sus credenciales." });
                        } else if (!response.ok) {
                            observer.error({ status: response.status, message: "Error en la solicitud." });
                        }
                        return response.json();
                    })
                    .then(data => {
                        this._livesSession.A3DocToken = data.token;
                        observer.next(data);
                        observer.complete();
                    })
                    .catch(error => observer.error(error));
            });
        }

        // Si isTest es false, utiliza this.appA3DocConnectionService.getModel() para obtener los datos de credenciales
        return this.appA3DocConnectionService.getModel().pipe(
            switchMap(credentialsData => {
                const headers = {
                    'Content-Type': 'application/json',
                    'Ocp-Apim-Subscription-Key': credentialsData.apiKey
                };

                const credentials = {
                    tenant: tenant || credentialsData.tenant,
                    user: user || credentialsData.user,
                    password: password || credentialsData.password,
                    deviceId: deviceId || credentialsData.deviceId,
                    deviceType: deviceType || credentialsData.deviceType,
                    devicePlatform: devicePlatform || credentialsData.devicePlatform
                };

                return from(
                    fetch(url, {
                        method: 'POST',
                        headers: headers,
                        body: JSON.stringify(credentials)
                    })
                ).pipe(
                    switchMap(response => {
                        if (response.status === 401) {
                            return throwError({ status: 400, message: "Error 400: Solicitud incorrecta. Revise sus credenciales." });
                        } else if (!response.ok) {
                            return throwError({ status: response.status, message: "Error en la solicitud." });
                        }
                        return from(response.json());
                    }),
                    map(data => {
                        this._livesSession.A3DocToken = data.token;
                        return data;
                    }),
                    catchError(error => throwError(error))
                );
            })
        );

    }

    async getTokenValue(): Promise<any> {
        const token =  this._livesSession.A3DocToken;
        if (token) {
            return token;
        } else {
            const response = await this.getToken().toPromise();
            return response.token;
        }
    }

    public async getLivesHrCategoriesIds(): Promise<any> {
        try {
            const data = await this.getCategoriesTree();
            const ids = this.filterLivesHrCategoriesIds(data.tree, 'LivesHR');
            return ids;
        } catch (error) {
            console.error('Error getLivesHrCategoriesIds:', error);
            throw error;
        }
    }

    async setCustomLivesHrCategories({ containerIds }): Promise<any> {
        const url = `${AppConfig.settings.apiServerPE.apiUrl}a3doc/custom/containers`
        try {
            const headers = new HttpHeaders({
                'X-API-KEY': AppConfig.settings.apiServerPE.apiKey
            });

            const body = {
                "environment":  this._livesSession.EnvironmentId,
                "containerIds": containerIds
            }

            const response = await this.http.post<any>(url, body, { headers }).toPromise();
            return response

        } catch (error) {
            console.error('Error al guardar categorias personalizadas', error);
            throw error;
        }

    }

    public async getCategoriesTree(isRetry: boolean = false, justIds: boolean = false): Promise<any> {

        const url = `${AppConfig.settings.apiServerPE.a3docUrl}/containerstree`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                const result = await this.getTokenValue();
                if (result?.status == 400) {
                    return []
                } else {
                    token = result
                }
            }

            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const sendData = {
                "token": token
            };

            const response = await this.http.post<any>(url, sendData, { headers }).toPromise();

            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');
                this._livesSession.removeA3DocToken();
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.getCategoriesTree(false, justIds);
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            if (justIds && response.tree && response.tree[0] && response.tree[0].children) {
                const ids = response.tree[0].children.map((child: any) => child.id);
                return ids;
            } else {
                return response;
            }

        } catch (error) {
            console.error('Error al obtener las categorías:', error);
            throw error;
        }
    }

    public async getDocumentsWithoutPayrollForHrUser(page: number, companyNif: string, isRetry: boolean = false, accumulatedResults: any[] = []): Promise<any[]> {
        try {
            // Obtener las categorías usando await
            const data = await this.getCategoriesTree(null);
            const categoriesIds = data.tree[0]?.children ? data.tree[0].children.map(child => child.id) : [];
            let categoriesA3Doc = formatCategoryKeyValue(data.tree);

            const url = `${AppConfig.settings.apiServerPE.a3docUrl}/getdocumentsextended`;

            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }

            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            // Uso directo del companyNif proporcionado

            const sendData = {
                "token": token,
                "ContainersIdstoSearch": categoriesIds,
                "SearchChildContainers": true,
                "ContainersIdsToIgnore": this.deleteCategoryTreesByIds(data.tree, true),
                "PageIndex": page,
                "PageSize": 100,
                "Filter": [
                    {
                        "AttributeId": 1,
                        "MatchWholeWord": true,
                        "FilterValue": companyNif
                    }
                ],
                "sort": "DESC",
                "order": "DATE"
            };

            const response = await this.http.post<any>(url, sendData, { headers }).toPromise();

            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');
                this._livesSession.removeA3DocToken();
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.getDocumentsWithoutPayrollForHrUser(page, companyNif, false, accumulatedResults);
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            // Filtrar y acumular los resultados de la página actual
            if (response.documents && response.documents.length > 0) {
                const filteredDocuments = response.documents.map(doc => {
                    const containersNames = doc.ContainersId.map(containerId => {
                        const category = categoriesA3Doc.find(cat => cat.id === containerId);
                        return category ? category.name : 'Categoría no encontrada';
                    }).join(', ');

                    const formattedDocument: AppA3DocDocument = {
                        id: doc.Id,
                        code: doc.Code,
                        title: doc.Title,
                        type: doc.Type,
                        size: doc.Size,
                        date: convertToISOString(doc.Date),
                        dateModified: doc.DateModified,
                        customerId: doc.CustomerId,
                        customerName: doc.CustomerName,
                        isAppDoc: doc.IsAppDoc,
                        containersId: doc.ContainersId,
                        containersNames,
                        attributes: doc.Attributes,
                        isNew: false
                    };

                    return formattedDocument;
                });
                accumulatedResults = accumulatedResults.concat(filteredDocuments);
            }

            // Llamar recursivamente a la siguiente página si es necesario
            if (accumulatedResults.length % 100 === 0) { // Si el tamaño de página se alcanza, intentar siguiente página
                return this.getDocumentsWithoutPayrollForHrUser(page + 1, companyNif, false, accumulatedResults);
            }

            return accumulatedResults;
        } catch (error) {
            console.error('Error al obtener los documentos:', error);
            throw error;
        }
    }

    public async getDocumentsWithoutPayrollForEmployee(page: number, companyNif: string, employeeNif: string = null, docsRead: {} = {}, isRetry: boolean = false, accumulatedResults: any[] = []): Promise<{ allDocs: any[], employeeDocs: any[] }> {
        try {
            // Obtener las categorías usando await
            const data = await this.getCategoriesTree(null);

            const categoriesIds = data.tree[0]?.children ? data.tree[0].children.map(child => child.id) : [];
            let categoriesA3Doc = formatCategoryKeyValue(data.tree);

            const url = `${AppConfig.settings.apiServerPE.a3docUrl}/getdocumentsextended`;

            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }

            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            // Uso directo del companyNif proporcionado
            const sendData = {
                "token": token,
                "ContainersIdstoSearch": categoriesIds,
                "SearchChildContainers": true,
                "ContainersIdsToIgnore": this.deleteCategoryTreesByIds(data.tree, true),
                "PageIndex": page,
                "PageSize": 100,
                "Filter": [
                    {
                        "AttributeId": 1,
                        "MatchWholeWord": true,
                        "FilterValue": companyNif
                    }
                ],
                "sort": "DESC",
                "order": "DATE"
            };

            const response = await this.http.post<any>(url, sendData, { headers }).toPromise();

            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');
                this._livesSession.removeA3DocToken();
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.getDocumentsWithoutPayrollForEmployee(page, companyNif, employeeNif, docsRead, true, accumulatedResults);
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            // Filtrar y acumular los resultados de la página actual
            if (response.documents && response.documents.length > 0) {
                const filteredDocuments = await Promise.all(response.documents.map(async (doc) => {
                    const docData = await this.getDoc(doc.Id); // Obtener el docData usando el ID del documento
                    const containersNames = doc.ContainersId.map(containerId => {
                        const category = categoriesA3Doc.find(cat => cat.id === containerId);
                        return category ? category.name : 'Categoría no encontrada';
                    }).join(', ');

                    const formattedDocument: AppA3DocDocument = {
                        id: doc.Id,
                        code: doc.Code,
                        title: doc.Title,
                        type: doc.Type,
                        size: doc.Size,
                        date: convertToISOString(doc.Date),
                        dateModified: doc.DateModified,
                        customerId: doc.CustomerId,
                        customerName: doc.CustomerName,
                        isAppDoc: doc.IsAppDoc,
                        containersId: doc.ContainersId,
                        containersNames,
                        attributes: doc.Attributes,
                        isNew: false,
                        docData: docData.content,
                        viewedAt: formatDate(docsRead[doc.Id]?.viewedAt) || ''
                    };

                    return formattedDocument;
                }));
                accumulatedResults = accumulatedResults.concat(filteredDocuments);
            }

            // Filtrar documentos donde AttributeId 802 coincida con el employeeNif
            const documentosFiltradosPorEmployeeNif = accumulatedResults.filter(doc =>
                doc.attributes.some(attr => attr.id === '802' && attr.value === employeeNif)
            );

            return {
                allDocs: accumulatedResults,
                employeeDocs: documentosFiltradosPorEmployeeNif
            };

        } catch (error) {
            console.error('Error al obtener los documentos:', error);
            throw error;
        }
    }

    public async getDoc(id: string, isRetry: boolean = false): Promise<any> {

        const url = `${AppConfig.settings.apiServerPE.a3docUrl}/download`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToSend = {
                "token": `${token}`,
                "documentId": `${id}`
            }


            const response = await this.http.post<any>(url, dataToSend, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.getDoc(id, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return response;
        } catch (error) {
            console.error('Error al obtener el documento', error);
            throw error;
        }
    }

    public async createDoc(data: any, isRetry: boolean = false): Promise<any> {

        const url = `${AppConfig.settings.apiServerPE.a3docUrl}uploadextended`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToCreate = formatCreateA3DocJson({ isAppDocument: false, token: token, content: data.docData, extension: data.mimeType, containersIds: data.category, companyNif: data.company.nif, companyName: data.company.name, innuvaCompanyCode: data.company.companyCode, docName: data.name })

            const response = await this.http.post<any>(url, dataToCreate, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.createDoc(data, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return response;
        } catch (error) {
            console.error('Error al crear el documento', error);
            throw error;
        }
    }

    public async deleteDoc(id: string, isRetry: boolean = false): Promise<any> {

        const url = `${AppConfig.settings.apiServerPE.a3docUrl}/deletedocument`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToSend = {
                "token": `${token}`,
                "documentId": `${id}`
            }


            const response = await this.http.post<any>(url, dataToSend, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.getDoc(id, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return response;
        } catch (error) {
            console.error('Error al obtener el documento', error);
            throw error;
        }
    }

    public async updateDoc(data: any, isRetry: boolean = false): Promise<any> {
        const url = `${AppConfig.settings.apiServerPE.a3docUrl}uploadextended`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToUpdate = formatUpdateA3DocJson({ token: token, documentData: data })
            // console.log(dataToUpdate)
            const response = await this.http.post<any>(url, dataToUpdate, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.updateDoc(data, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return response;
        } catch (error) {
            console.error('Error al actualizar el documento', error);
            throw error;
        }
    }

    public async updateDocUsers(data: any, users: any, overWrite: boolean = true, isRetry: boolean = false): Promise<any> {

        const url = `${AppConfig.settings.apiServerPE.a3docUrl}uploadextended`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToUpdate = overWrite ? formatUpdateA3DocUsersJson({ token: token, documentData: data, users: users }) : formatUpdateA3DocUsersOnboardingJson({ token: token, documentData: data, users: users })

            const response = await this.http.post<any>(url, dataToUpdate, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.updateDocUsers(data, users, overWrite, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return {
                response,
                attributes: dataToUpdate.DocumentAttributes
            };
        } catch (error) {
            console.error('Error al actualizar el documento', error);
            throw error;
        }

    }

    public async deleteDocUsers(data: any, user: any, usersSelectedInfo: any, isRetry: boolean = false): Promise<any> {
        const url = `${AppConfig.settings.apiServerPE.a3docUrl}uploadextended`;

        try {
            let token =  this._livesSession.A3DocToken;
            if (!token) {
                token = await this.getTokenValue();
            }


            const credentialsData = await this.appA3DocConnectionService.getModel().toPromise();

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Ocp-Apim-Subscription-Key': credentialsData.apiKey
            });

            const dataToUpdate = formatDeleteA3DocUsersJson({ token: token, documentData: data, userToDelete: user, users: usersSelectedInfo })

            const response = await this.http.post<any>(url, dataToUpdate, { headers }).toPromise();
            // Verificar si la respuesta contiene errorcode = 6
            if (response.errorCode === 6) {
                console.error('Error: Código de error 6 recibido');

                // Borrar el token de session
                this._livesSession.removeA3DocToken();

                // Si aún no hemos reintentado, volver a llamar al método
                if (!isRetry) {
                    console.error('Token inválido, obteniendo un nuevo token e intentando de nuevo...');
                    return this.deleteDocUsers(data, user, usersSelectedInfo, true); // Reintentar solo una vez
                } else {
                    throw new Error('El token es inválido o ha expirado incluso después de reintentar.');
                }
            }

            return {
                response,
                attributes: dataToUpdate.DocumentAttributes
            };
        } catch (error) {
            console.error('Error al actualizar el documento', error);
            throw error;
        }

    }

    deleteCategoryTreesByIds(tree: any[], returnJustIds: boolean = false): any[] {
        const idsToRemove = [640, 200, 500, 15, 625];
        let removedIds: number[] = [];

        // Función recursiva para obtener todos los IDs de los nodos hijos
        const collectDescendantIds = (node: any) => {
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => {
                    removedIds.push(child.id); // Agregar ID de los hijos
                    collectDescendantIds(child); // Recursividad para los descendientes
                });
            }
        };

        const traverse = (nodes: any[]): any[] => {
            return nodes
                .filter(node => {
                    if (idsToRemove.includes(node.id)) {
                        removedIds.push(node.id); // Agregar el ID eliminado al array
                        collectDescendantIds(node); // Agregar IDs de todos los hijos
                        return false; // Eliminar el nodo
                    }
                    return true; // Mantener el nodo
                })
                .map(node => ({
                    ...node,
                    children: node.children ? traverse(node.children) : [] // Recursión para los hijos
                }));
        };

        const result = traverse(tree);

        // Si returnJustIds es true, devolver solo los IDs eliminados, sino, la estructura filtrada
        return returnJustIds ? removedIds : result;
    }

    filterLivesHrCategoriesIds(tree: any[], searchString: string): number[] {
        let collectedIds: number[] = [];
        /* DEVOLVER PADRES E HIJOS */
        /*  // Función recursiva para obtener todos los IDs de los nodos hijos
         const collectDescendantIds = (node: any) => {
             collectedIds.push(node.id); // Agregar ID del nodo
             if (node.children && node.children.length > 0) {
                 node.children.forEach(child => {
                     collectDescendantIds(child); // Recursividad para los descendientes
                 });
             }
         };
 
         // Función recursiva para recorrer el árbol
         const traverse = (nodes: any[]): any => {
             nodes.forEach(node => {
                 if (node.name === searchString) {
                     collectDescendantIds(node); // Si el valor coincide, recolectar IDs
                 }
                 if (node.children && node.children.length > 0) {
                     traverse(node.children); // Recursión para los hijos
                 }
             });
         }; */


        /* DEVOLVER SOLO EL PADRE */
        // Función recursiva para recorrer el árbol
        const traverse = (nodes: any[]): any => {
            nodes.forEach(node => {
                if (node.name === searchString) {
                    collectedIds.push(node.id); // Agregar solo el ID del nodo padre cuando coincida
                }
                if (node.children && node.children.length > 0) {
                    traverse(node.children); // Recursión para los hijos
                }
            });
        };

        traverse(tree);
        return collectedIds; // Retornar los IDs encontrados
    }

    async getReadDocsByUser({ user }): Promise<any> {
        const url = `${AppConfig.settings.apiServerPE.apiUrl}a3docuser/documents/viewed/${user}`;
        try {
            const headers = new HttpHeaders({
                'X-API-KEY': AppConfig.settings.apiServerPE.apiKey
            });
            const response = await this.http.get<any>(url, { headers }).toPromise();
            return response;
        } catch (error) {
            console.error('Error al obtener los documentos leídos por el usuario', error);
            throw error;
        }
    }

    async getDocumentReaders({ document }): Promise<any> {
        const url = `${AppConfig.settings.apiServerPE.apiUrl}a3docuser/document/views/${document}`;
        try {
            const headers = new HttpHeaders({
                'X-API-KEY': AppConfig.settings.apiServerPE.apiKey
            });
            const response = await this.http.get<any>(url, { headers }).toPromise();
            return response

        } catch (error) {
            console.error('Error al actualizar el documento', error);
            throw error;
        }

    }


}